import type { AddressInfo } from '../api/generated/pm';

/**
 * Address format strings per ISO 3166-1 alpha-2 country code.
 *
 * Format tokens:
 *   %A = street address (addressLineOne + addressLineTwo joined)
 *   %C = city/locality
 *   %S = state/province/region
 *   %Z = zip/postal code
 *   %D = dependent locality  – not in our model, rendered as empty
 *   %X = sorting code        – not in our model, rendered as empty
 *   %n = line separator (rendered as ", " in compact single-line display)
 *
 * Scoped to the countries available in `@topcon/topcon-utils` `countries`.
 * The country is appended at the end by formatAddress().
 *
 * %N = customer number
 * %O = organization name
 * %A = address line one
 * %C = city/locality
 * %S = state/province/region
 * %Z = zip/postal code
 */
const COUNTRY_FORMATS: Readonly<Record<string, string>> = {
  AE: '%N%n%O%n%A%n%S',
  AL: '%N%n%O%n%A%n%Z%n%C',
  AM: '%A%n%Z%n%C%n%S',
  AR: '%N%n%O%n%A%n%Z %C%n%S',
  AT: '%O%n%N%n%A%n%Z %C',
  AU: '%O%n%N%n%A%n%C %S %Z',
  AZ: '%N%n%O%n%A%nAZ %Z %C',
  BA: '%N%n%O%n%A%n%Z %C',
  BD: '%N%n%O%n%A%n%C - %Z',
  BE: '%O%n%N%n%A%n%Z %C',
  BG: '%N%n%O%n%A%n%Z %C',
  BH: '%N%n%O%n%A%n%C %Z',
  BN: '%N%n%O%n%A%n%C %Z',
  BO: '%N%n%O%n%A%n%C',
  BR: '%O%n%N%n%A%n%D%n%C-%S%n%Z',
  BY: '%O%n%N%n%A%n%Z, %C%n%S',
  CA: '%N%n%O%n%A%n%C %S %Z',
  CH: '%O%n%N%n%A%nCH-%Z %C',
  CI: '%N%n%O%n%X %A %C %X',
  CL: '%N%n%O%n%A%n%Z %C%n%S',
  CN: '%Z%n%S%C%D%n%A%n%O%n%N',
  CO: '%N%n%O%n%A%n%D%n%C, %S, %Z',
  CR: '%N%n%O%n%A%n%S, %C%n%Z',
  CU: '%N%n%O%n%A%n%C %S%n%Z',
  CV: '%N%n%O%n%A%n%Z %C%n%S',
  CY: '%N%n%O%n%A%n%Z %C',
  CZ: '%N%n%O%n%A%n%Z %C',
  DE: '%N%n%O%n%A%n%Z %C',
  DK: '%N%n%O%n%A%n%Z %C',
  DO: '%N%n%O%n%A%n%Z %C',
  DZ: '%N%n%O%n%A%n%Z %C',
  EC: '%N%n%O%n%A%n%Z%n%C',
  EE: '%N%n%O%n%A%n%Z %C %S',
  EG: '%N%n%O%n%A%n%C%n%S%n%Z',
  ES: '%N%n%O%n%A%n%Z %C %S',
  ET: '%N%n%O%n%A%n%Z %C',
  FI: '%O%n%N%n%A%nFI-%Z %C',
  FK: '%N%n%O%n%A%n%C%n%Z',
  FR: '%O%n%N%n%A%n%Z %C',
  GB: '%N%n%O%n%A%n%C%n%Z',
  GE: '%N%n%O%n%A%n%Z %C',
  GF: '%O%n%N%n%A%n%Z %C %X',
  GG: '%N%n%O%n%A%n%C%nGUERNSEY%n%Z',
  GI: '%N%n%O%n%A%nGIBRALTAR%n%Z',
  GL: '%N%n%O%n%A%n%Z %C',
  GP: '%O%n%N%n%A%n%Z %C %X',
  GR: '%N%n%O%n%A%n%Z %C',
  GT: '%N%n%O%n%A%n%Z- %C',
  HK: '%S%n%C%n%A%n%O%n%N',
  HN: '%N%n%O%n%A%n%C, %S%n%Z',
  HR: '%N%n%O%n%A%nHR-%Z %C',
  HT: '%N%n%O%n%A%nHT%Z %C',
  HU: '%N%n%O%n%C%n%A%n%Z',
  ID: '%N%n%O%n%A%n%C%n%S %Z',
  IE: '%N%n%O%n%A%n%D%n%C%n%S%n%Z',
  IL: '%N%n%O%n%A%n%C %Z',
  IM: '%N%n%O%n%A%n%C%n%Z',
  IN: '%N%n%O%n%A%n%C %Z%n%S',
  IQ: '%O%n%N%n%A%n%C, %S%n%Z',
  IR: '%O%n%N%n%S%n%C, %D%n%A%n%Z',
  IS: '%N%n%O%n%A%n%Z %C',
  IT: '%N%n%O%n%A%n%Z %C %S',
  JE: '%N%n%O%n%A%n%C%nJERSEY%n%Z',
  JM: '%N%n%O%n%A%n%C%n%S %X',
  JO: '%N%n%O%n%A%n%C %Z',
  JP: '〒%Z%n%S%n%A%n%O%n%N',
  KE: '%N%n%O%n%A%n%C%n%Z',
  KG: '%N%n%O%n%A%n%Z %C',
  KH: '%N%n%O%n%A%n%C %Z',
  KN: '%N%n%O%n%A%n%C, %S',
  KP: '%Z%n%S%n%C%n%A%n%O%n%N',
  KR: '%S %C%D%n%A%n%O%n%N%n%Z',
  KW: '%N%n%O%n%A%n%Z %C',
  KY: '%N%n%O%n%A%n%S %Z',
  KZ: '%Z%n%S%n%C%n%A%n%O%n%N',
  LA: '%N%n%O%n%A%n%Z %C',
  LB: '%N%n%O%n%A%n%C %Z',
  LI: '%O%n%N%n%A%nFL-%Z %C',
  LK: '%N%n%O%n%A%n%C%n%Z',
  LS: '%N%n%O%n%A%n%C %Z',
  LT: '%O%n%N%n%A%nLT-%Z %C %S',
  LU: '%O%n%N%n%A%nL-%Z %C',
  LV: '%N%n%O%n%A%n%S%n%C, %Z',
  MA: '%N%n%O%n%A%n%Z %C',
  MC: '%N%n%O%n%A%nMC-%Z %C %X',
  MD: '%N%n%O%n%A%nMD-%Z %C',
  ME: '%N%n%O%n%A%n%Z %C',
  MG: '%N%n%O%n%A%n%Z %C',
  MK: '%N%n%O%n%A%n%Z %C',
  MN: '%N%n%O%n%A%n%C%n%S %Z',
  MP: '%N%n%O%n%A%n%C %S %Z',
  MQ: '%O%n%N%n%A%n%Z %C %X',
  MT: '%N%n%O%n%A%n%C %Z',
  MU: '%N%n%O%n%A%n%Z%n%C',
  MV: '%N%n%O%n%A%n%C %Z',
  MX: '%N%n%O%n%A%n%D%n%Z %C, %S',
  MY: '%N%n%O%n%A%n%D%n%Z %C%n%S',
  NA: '%N%n%O%n%A%n%C%n%Z',
  NC: '%O%n%N%n%A%n%Z %C %X',
  NE: '%N%n%O%n%A%n%Z %C',
  NG: '%N%n%O%n%A%n%D%n%C %Z%n%S',
  NI: '%N%n%O%n%A%n%Z%n%C, %S',
  NL: '%O%n%N%n%A%n%Z %C',
  NO: '%N%n%O%n%A%n%Z %C',
  NP: '%N%n%O%n%A%n%C %Z',
  NZ: '%N%n%O%n%A%n%D%n%C %Z',
  OM: '%N%n%O%n%A%n%Z%n%C',
  PA: '%N%n%O%n%A%n%C%n%S',
  PE: '%N%n%O%n%A%n%C %Z%n%S',
  PG: '%N%n%O%n%A%n%C %Z %S',
  PH: '%N%n%O%n%A%n%D, %C%n%Z %S',
  PK: '%N%n%O%n%A%n%D%n%C-%Z',
  PL: '%N%n%O%n%A%n%Z %C',
  PM: '%O%n%N%n%A%n%Z %C %X',
  PR: '%N%n%O%n%A%n%C PR %Z',
  PT: '%N%n%O%n%A%n%Z %C',
  PW: '%N%n%O%n%A%n%C %S %Z',
  PY: '%N%n%O%n%A%n%Z %C',
  RE: '%O%n%N%n%A%n%Z %C %X',
  RO: '%N%n%O%n%A%n%Z %S %C',
  RS: '%N%n%O%n%A%n%Z %C',
  RU: '%N%n%O%n%A%n%C%n%S%n%Z',
  SA: '%N%n%O%n%A%n%C %Z',
  SD: '%N%n%O%n%A%n%C%n%Z',
  SE: '%O%n%N%n%A%nSE-%Z %C',
  SG: '%N%n%O%n%A%nSINGAPORE %Z',
  SH: '%N%n%O%n%A%n%C%n%Z',
  SI: '%N%n%O%n%A%nSI-%Z %C',
  SJ: '%N%n%O%n%A%n%Z %C',
  SK: '%N%n%O%n%A%n%Z %C',
  SM: '%N%n%O%n%A%n%Z %C',
  SN: '%N%n%O%n%A%n%Z %C',
  SO: '%N%n%O%n%A%n%C, %S %Z',
  SR: '%N%n%O%n%A%n%C%n%S',
  SV: '%N%n%O%n%A%n%Z-%C%n%S',
  SZ: '%N%n%O%n%A%n%C%n%Z',
  TC: '%N%n%O%n%A%n%C%n%Z',
  TH: '%N%n%O%n%A%n%D %C%n%S %Z',
  TJ: '%N%n%O%n%A%n%Z %C',
  TM: '%N%n%O%n%A%n%Z %C',
  TN: '%N%n%O%n%A%n%Z %C',
  TR: '%N%n%O%n%A%n%Z %C/%S',
  TV: '%N%n%O%n%A%n%C%n%S',
  TW: '%Z%n%S%C%n%A%n%O%n%N',
  TZ: '%N%n%O%n%A%n%Z %C',
  UA: '%N%n%O%n%A%n%C%n%S%n%Z',
  UM: '%N%n%O%n%A%n%C %S %Z',
  US: '%N%n%O%n%A%n%C, %S %Z',
  UY: '%N%n%O%n%A%n%Z %C %S',
  UZ: '%N%n%O%n%A%n%Z %C%n%S',
  VA: '%N%n%O%n%A%n%Z %C',
  VE: '%N%n%O%n%A%n%C %Z, %S',
  VG: '%N%n%O%n%A%n%C%n%Z',
  VI: '%N%n%O%n%A%n%C %S %Z',
  VN: '%N%n%O%n%A%n%C%n%S %Z',
  WF: '%O%n%N%n%A%n%Z %C %X',
  YE: '%A%n%C%n%S',
  ZA: '%N%n%O%n%A%n%D%n%C%n%Z',
  ZM: '%N%n%O%n%A%n%Z %C',
};

const DEFAULT_FORMAT = '%A%n%C';

/**
 * Maps ISO 3166-1 alpha-3 codes to alpha-2.
 * Covers exactly the countries present in `@topcon/topcon-utils` `countries`,
 * allowing the formatter to handle API responses that use either variant
 * (e.g. "AUS" → "AU", "GBR" → "GB").
 */
const ALPHA3_TO_ALPHA2: Readonly<Record<string, string>> = {
  ABW: 'AW',
  AFG: 'AF',
  AGO: 'AO',
  AIA: 'AI',
  ALA: 'AX',
  ALB: 'AL',
  AND: 'AD',
  ARE: 'AE',
  ARG: 'AR',
  ARM: 'AM',
  ASM: 'AS',
  ATA: 'AQ',
  ATF: 'TF',
  ATG: 'AG',
  AUS: 'AU',
  AUT: 'AT',
  AZE: 'AZ',
  BDI: 'BI',
  BEL: 'BE',
  BEN: 'BJ',
  BFA: 'BF',
  BGD: 'BD',
  BGR: 'BG',
  BHR: 'BH',
  BHS: 'BS',
  BIH: 'BA',
  BLM: 'BL',
  BLR: 'BY',
  BLZ: 'BZ',
  BMU: 'BM',
  BOL: 'BO',
  BRA: 'BR',
  BRB: 'BB',
  BRN: 'BN',
  BTN: 'BT',
  BVT: 'BV',
  BWA: 'BW',
  CAF: 'CF',
  CAN: 'CA',
  CCK: 'CC',
  CHE: 'CH',
  CHL: 'CL',
  CHN: 'CN',
  CIV: 'CI',
  CMR: 'CM',
  COD: 'CD',
  COG: 'CG',
  COK: 'CK',
  COL: 'CO',
  COM: 'KM',
  CPV: 'CV',
  CRI: 'CR',
  CUB: 'CU',
  CUW: 'CW',
  CXR: 'CX',
  CYM: 'KY',
  CYP: 'CY',
  CZE: 'CZ',
  DEU: 'DE',
  DJI: 'DJ',
  DMA: 'DM',
  DNK: 'DK',
  DOM: 'DO',
  DZA: 'DZ',
  ECU: 'EC',
  EGY: 'EG',
  ERI: 'ER',
  ESH: 'EH',
  ESP: 'ES',
  EST: 'EE',
  ETH: 'ET',
  FIN: 'FI',
  FJI: 'FJ',
  FLK: 'FK',
  FRA: 'FR',
  FRO: 'FO',
  FSM: 'FM',
  GAB: 'GA',
  GBR: 'GB',
  GEO: 'GE',
  GGY: 'GG',
  GHA: 'GH',
  GIB: 'GI',
  GIN: 'GN',
  GLP: 'GP',
  GMB: 'GM',
  GNB: 'GW',
  GNQ: 'GQ',
  GRC: 'GR',
  GRD: 'GD',
  GRL: 'GL',
  GTM: 'GT',
  GUF: 'GF',
  GUM: 'GU',
  GUY: 'GY',
  HKG: 'HK',
  HMD: 'HM',
  HND: 'HN',
  HRV: 'HR',
  HTI: 'HT',
  HUN: 'HU',
  IDN: 'ID',
  IMN: 'IM',
  IND: 'IN',
  IOT: 'IO',
  IRL: 'IE',
  IRN: 'IR',
  IRQ: 'IQ',
  ISL: 'IS',
  ISR: 'IL',
  ITA: 'IT',
  JAM: 'JM',
  JEY: 'JE',
  JOR: 'JO',
  JPN: 'JP',
  KAZ: 'KZ',
  KEN: 'KE',
  KGZ: 'KG',
  KHM: 'KH',
  KIR: 'KI',
  KNA: 'KN',
  KOR: 'KR',
  KWT: 'KW',
  LAO: 'LA',
  LBN: 'LB',
  LBR: 'LR',
  LBY: 'LY',
  LCA: 'LC',
  LIE: 'LI',
  LKA: 'LK',
  LSO: 'LS',
  LTU: 'LT',
  LUX: 'LU',
  LVA: 'LV',
  MAC: 'MO',
  MAF: 'MF',
  MAR: 'MA',
  MCO: 'MC',
  MDA: 'MD',
  MDG: 'MG',
  MDV: 'MV',
  MEX: 'MX',
  MHL: 'MH',
  MKD: 'MK',
  MLI: 'ML',
  MLT: 'MT',
  MMR: 'MM',
  MNG: 'MN',
  MNE: 'ME',
  MNP: 'MP',
  MOZ: 'MZ',
  MRT: 'MR',
  MSR: 'MS',
  MTQ: 'MQ',
  MUS: 'MU',
  MWI: 'MW',
  MYS: 'MY',
  MYT: 'YT',
  NAM: 'NA',
  NCL: 'NC',
  NER: 'NE',
  NFK: 'NF',
  NGA: 'NG',
  NIC: 'NI',
  NIU: 'NU',
  NLD: 'NL',
  NOR: 'NO',
  NPL: 'NP',
  NRU: 'NR',
  NZL: 'NZ',
  OMN: 'OM',
  PAK: 'PK',
  PAN: 'PA',
  PCN: 'PN',
  PER: 'PE',
  PHL: 'PH',
  PLW: 'PW',
  PNG: 'PG',
  POL: 'PL',
  PRI: 'PR',
  PRK: 'KP',
  PRY: 'PY',
  PSE: 'PS',
  PRT: 'PT',
  PYF: 'PF',
  QAT: 'QA',
  REU: 'RE',
  ROU: 'RO',
  RUS: 'RU',
  RWA: 'RW',
  SAU: 'SA',
  SDN: 'SD',
  SEN: 'SN',
  SGP: 'SG',
  SGS: 'GS',
  SHN: 'SH',
  SJM: 'SJ',
  SLB: 'SB',
  SLE: 'SL',
  SLV: 'SV',
  SMR: 'SM',
  SOM: 'SO',
  SPM: 'PM',
  SRB: 'RS',
  SSD: 'SS',
  STP: 'ST',
  SUR: 'SR',
  SVK: 'SK',
  SVN: 'SI',
  SWE: 'SE',
  SWZ: 'SZ',
  SXM: 'SX',
  SYC: 'SC',
  SYR: 'SY',
  TCA: 'TC',
  TCD: 'TD',
  TGO: 'TG',
  THA: 'TH',
  TJK: 'TJ',
  TKL: 'TK',
  TKM: 'TM',
  TLS: 'TL',
  TON: 'TO',
  TTO: 'TT',
  TUN: 'TN',
  TUR: 'TR',
  TUV: 'TV',
  TWN: 'TW',
  TZA: 'TZ',
  UGA: 'UG',
  UKR: 'UA',
  UMI: 'UM',
  URY: 'UY',
  USA: 'US',
  UZB: 'UZ',
  VAT: 'VA',
  VCT: 'VC',
  VEN: 'VE',
  VGB: 'VG',
  VIR: 'VI',
  VNM: 'VN',
  VUT: 'VU',
  WLF: 'WF',
  WSM: 'WS',
  YEM: 'YE',
  ZAF: 'ZA',
  ZMB: 'ZM',
  ZWE: 'ZW',
};

const resolveAlpha2 = (country: string): string => {
  const upperCountry = country.toUpperCase();
  if (upperCountry.length === 3) return ALPHA3_TO_ALPHA2[upperCountry] ?? upperCountry;
  return upperCountry;
};

const getTokenValue = (token: string, address: AddressInfo): string | null => {
  switch (token) {
    case 'A': {
      const val = [address.addressLineOne, address.addressLineTwo].filter(Boolean).join(', ');
      return val.trim() ?? null;
    }
    case 'C': {
      const trimmedCity = address.city?.trim();
      return trimmedCity ? trimmedCity : null;
    }
    case 'S': {
      const trimmedState = address.stateProvinceRegion?.trim();
      return trimmedState ? trimmedState : null;
    }
    case 'Z': {
      const trimmedZip = address.zipPostalCode?.trim();
      return trimmedZip ? trimmedZip : null;
    }
    default:
      return null;
  }
};

const cleanSegment = (segment: string): string =>
  segment
    .replace(/\s{2,}/g, ' ')
    .replace(/^[,\s]+/, '')
    .replace(/[,\s]+$/, '')
    .trim();

export const formatAddress = (address: AddressInfo): string => {
  const alpha2 = resolveAlpha2(address.country ?? '');
  const fmt = COUNTRY_FORMATS[alpha2] ?? DEFAULT_FORMAT;

  const segments = fmt
    .split('%n')
    .map((segment) => {
      let hasValidTokenData = false;
      const replaced = segment.replace(/%([ACSZNDOX])/g, (_, token: string) => {
        const val = getTokenValue(token, address);
        if (val !== null) {
          hasValidTokenData = true;
          return val;
        }
        return '';
      });
      if (!hasValidTokenData && /%[ACSZNDOX]/.test(segment)) {
        return '';
      }

      return cleanSegment(replaced);
    })
    .filter((segment) => /[\p{L}\p{N}]/u.test(segment));

  if (address.country?.trim()) {
    segments.push(address.country.trim());
  }

  return segments.join(', ');
};
